import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { subscriptionTypesApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Check } from 'lucide-react';

export default function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionTypes();
  }, []);

  const fetchSubscriptionTypes = async () => {
    try {
      const response = await subscriptionTypesApi.getAll();
      setSubscriptionTypes(response.data);
    } catch (error) {
      console.error('Error fetching subscription types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (subscriptionTypeId) => {
    if (isAuthenticated) {
      navigate(`/dashboard?subscribe=${subscriptionTypeId}`);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              {t('pricing.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('pricing.subtitle')}
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : subscriptionTypes.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">{t('common.noResults')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {subscriptionTypes.map((type, index) => (
                <motion.div
                  key={type.id}
                  className="bento-card trace-beam flex flex-col"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`pricing-card-${type.name.toLowerCase()}`}
                >
                  {/* Icon/Image */}
                  {type.icon_url ? (
                    <img 
                      src={type.icon_url} 
                      alt={type.name} 
                      className="w-12 h-12 object-contain mb-6"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-6">
                      <span className="font-heading font-bold text-lg">{type.name[0]}</span>
                    </div>
                  )}

                  {/* Name */}
                  <h3 className="font-heading text-2xl font-semibold mb-2">
                    {type.name}
                  </h3>

                  {/* Description */}
                  {type.description && (
                    <p className="text-sm text-muted-foreground mb-4">
                      {type.description}
                    </p>
                  )}

                  {/* Price */}
                  <div className="mb-6">
                    <span className="font-heading text-4xl font-bold">
                      {formatCurrency(type.price)}
                    </span>
                    <span className="text-muted-foreground">{t('pricing.perMonth')}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" strokeWidth={2} />
                      <span>{t('pricing.features.instantAccess')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" strokeWidth={2} />
                      <span>{t('pricing.features.verificationCodes')}</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" strokeWidth={2} />
                      <span>{t('pricing.features.autoRenewal')}</span>
                    </li>
                  </ul>

                  {/* CTA */}
                  <Button 
                    className="w-full btn-scale"
                    disabled={!type.is_active}
                    onClick={() => handleSubscribe(type.id)}
                    data-testid={`subscribe-btn-${type.name.toLowerCase()}`}
                  >
                    {type.is_active ? t('pricing.subscribe') : t('pricing.unavailable')}
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
